import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Building2 } from "lucide-react";
import { ENVIRONMENTS } from "@/lib/media";

export default function Environments() {
  const scrollerRef = useRef(null);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: "smooth" });
  };

  return (
    <section id="environments" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-8 mb-10">
          <div className="md:col-span-7">
            <div className="section-eyebrow mb-5"><Building2 size={12} /> Environments We Work In</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A2540] leading-tight">
              From boardroom to shop floor — we go where the work happens.
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end justify-between md:justify-end gap-3">
            <p className="text-slate-600 leading-relaxed text-sm hidden md:block max-w-xs">
              Direct observation. Hands-on diagnosis. Practical solutions designed in the real environment.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => scrollBy(-1)} data-testid="env-prev" className="h-10 w-10 grid place-items-center bg-white border border-slate-300 hover:border-[#0A2540] transition-colors" aria-label="Scroll left">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => scrollBy(1)} data-testid="env-next" className="h-10 w-10 grid place-items-center bg-[#0A2540] text-white hover:bg-[#061A30] transition-colors" aria-label="Scroll right">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4 -mx-6 px-6 md:mx-0 md:px-0"
        >
          {ENVIRONMENTS.map((e, i) => (
            <motion.div
              key={e.t}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
              className="snap-start shrink-0 w-[280px] sm:w-[320px] group cursor-default"
            >
              <div className="aspect-[4/5] overflow-hidden bg-slate-200 relative">
                <img
                  src={e.src}
                  alt={e.t}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/85 via-[#0A2540]/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#10B981] mb-1">Environment</div>
                  <h3 className="font-display font-semibold text-white text-lg leading-tight">{e.t}</h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
